// Phase-A R1.2 — route-id → breadcrumb label. Static dictionary.
// Phase-B will layer dynamic context-aware labels (e.g., resolved course
// title for `/course/:courseId`) via a future resolver hook; R1.2 keeps
// the surface minimal — just a lookup and a fallback to the raw id/param.
//
// S-I18N-W6-SWEEP — the values are no longer Persian strings but KEYS into
// the `shell` catalogue (apps/web/src/locales/<lang>/shell.json → `breadcrumb.*`).
// Why `shell` and not a namespace of its own: breadcrumbs render on every
// workspace screen, and `shell` is one of the two BOOT namespaces awaited
// before the first paint (src/i18n/index.ts) — a lazy namespace here would
// print raw keys for one round-trip on a slow network, which is exactly the
// leak `route-namespaces.ts` was written to stop.
//
// The map itself stays exported and keyed by route id because
// `tests/nav/nav-coherence.test.ts` sweeps every sidebar-reachable segment
// through it; only the value's meaning changed (label → key).

import type { TFunction } from "i18next";
import { translate } from "../i18n/locale-state";

export const BREADCRUMB_LABELS: Record<string, string> = {
  home: "breadcrumb.home", programs: "breadcrumb.programs", catalog: "breadcrumb.catalog",
  "my-courses": "breadcrumb.myCourses",
  progress: "breadcrumb.progress", tutor: "breadcrumb.tutor", classroom: "breadcrumb.classroom",
  dashboard: "breadcrumb.dashboard",
  course: "breadcrumb.course", "course-live": "breadcrumb.courseLive", instructor: "breadcrumb.instructor",
  admissions: "breadcrumb.admissions", credential: "breadcrumb.credential", search: "breadcrumb.search",
  assessment: "breadcrumb.assessment",
  "assessment-live": "breadcrumb.assessmentLive", community: "breadcrumb.community",
  analytics: "breadcrumb.analytics",
  authoring: "breadcrumb.authoring", recordings: "breadcrumb.recordings", audit: "breadcrumb.audit",
  login: "breadcrumb.login", register: "breadcrumb.register", forgot: "breadcrumb.forgot",
  "verify-email": "breadcrumb.verifyEmail",
  "2fa-setup": "breadcrumb.twoFactorSetup", onboarding: "breadcrumb.onboarding",
  settings: "breadcrumb.settings",
  // S-2 nav fix — labels for the /settings/:tab deep-link segments so the
  // crumb reads «تنظیمات › امنیت» instead of a raw «security».
  security: "breadcrumb.security", privacy: "breadcrumb.privacy",
  notifications: "profile:settings.common.nav.notifications",
  calendar: "breadcrumb.calendar", library: "breadcrumb.library", help: "breadcrumb.help",
  pricing: "breadcrumb.pricing",
  faculty: "breadcrumb.faculty", admin: "breadcrumb.admin", parent: "breadcrumb.parent",
  officehours: "breadcrumb.officehours",
  events: "breadcrumb.events", about: "breadcrumb.about", schools: "breadcrumb.schools",
  labs: "breadcrumb.labs",
  virtuallab: "breadcrumb.virtuallab", research: "breadcrumb.research", inbox: "breadcrumb.inbox",
  messages: "breadcrumb.messages", bookmarks: "breadcrumb.bookmarks",
  achievements: "breadcrumb.achievements",
  submission: "breadcrumb.submission", profile: "breadcrumb.profile", transcript: "breadcrumb.transcript",
  "degree-audit": "breadcrumb.degreeAudit", registration: "breadcrumb.registration",
  career: "breadcrumb.career",
  "financial-aid": "breadcrumb.financialAid", wellness: "breadcrumb.wellness", alumni: "breadcrumb.alumni",
  // S-ASSESS-CENTER W2 — سنجش شایستگی (مسیر sidenav جدید، پاسخِ Q3 پلن)
  assess: "breadcrumb.assess",
  // S-ASSESS-W3b — کنسولِ ارزیاب/سفرِ داوطلب. مجزا از "assess" بالا (تلهٔ نامِ ماژول، پلن §۰).
  "assess-center": "breadcrumb.assessCenter", exercise: "breadcrumb.exercise",
  candidate: "breadcrumb.candidate", my: "breadcrumb.my",
  washup: "breadcrumb.washup",
  // S-ASSESS-W5b — گزارشِ ر۱ داوطلب («reports» برایِ ر۲ از قبل در خطِ پایین‌تر هست
  "my-report": "breadcrumb.myReport",
  hackathons: "breadcrumb.hackathons", "honor-code": "breadcrumb.honorCode", vista: "breadcrumb.vista",
  // R-SIS routes (انتخاب واحد، چارت، پیشخوان خدمت + آستانه‌ها/گردش‌کار/نیم‌سال در زیرشاخهٔ مدیریت)
  "add-drop": "breadcrumb.addDrop", "curriculum-builder": "breadcrumb.curriculumBuilder",
  "service-desk": "breadcrumb.serviceDesk", new: "breadcrumb.new",
  terms: "breadcrumb.terms", "degree-policies": "breadcrumb.degreePolicies",
  workflows: "breadcrumb.workflows",
  // R-PAY routes
  tuition: "breadcrumb.tuition", "tuition-config": "breadcrumb.tuitionConfig",
  // S-CARD W0-cfg-b گامِ ۲ / W5 / W4 (دایرکتوری)
  "membership-config": "breadcrumb.membershipConfig", membership: "breadcrumb.membership",
  "membership-queue": "breadcrumb.membershipQueue", directory: "breadcrumb.directory",
  // R-MARKET P1 — «بازار دانش» authoring studio; P5 — economics surfaces;
  // P7 — in-browser recording studio + Q&A/payout (recording label below).
  market: "breadcrumb.market", studio: "breadcrumb.studio", earnings: "breadcrumb.earnings",
  record: "breadcrumb.record", wishlist: "breadcrumb.wishlist",
  // S-21 — labels for the routes that grew AFTER R1.2 (the nav-coherence
  // vitest asserts every sidebar-reachable segment has one). Second-segment
  // words (hr, finance, …) label the `param` crumb of two-segment admin routes.
  wallet: "breadcrumb.wallet", agents: "breadcrumb.agents", checkout: "breadcrumb.checkout",
  "my-grades": "breadcrumb.myGrades", "my-attendance": "breadcrumb.myAttendance",
  "my-path": "breadcrumb.myPath",
  assignments: "breadcrumb.assignments", exam: "breadcrumb.exam",
  "assessment-builder": "breadcrumb.assessmentBuilder",
  "question-bank": "breadcrumb.questionBank", "item-analysis": "breadcrumb.itemAnalysis",
  "grading-queue": "breadcrumb.gradingQueue", "proctor-review": "breadcrumb.proctorReview",
  "live-class": "breadcrumb.liveClass", "grade-submission": "breadcrumb.gradeSubmission",
  roster: "breadcrumb.roster", attendance: "breadcrumb.attendance",
  "class-analytics": "breadcrumb.classAnalytics",
  "instructor-dashboard": "breadcrumb.instructorDashboard", ta: "breadcrumb.ta",
  "faculty-dashboard": "breadcrumb.facultyDashboard", commission: "breadcrumb.commission",
  hr: "breadcrumb.hr", regulation: "breadcrumb.regulation", finance: "breadcrumb.finance",
  withdrawals: "breadcrumb.withdrawals", people: "breadcrumb.people",
  org: "breadcrumb.org", moderate: "breadcrumb.moderate", support: "breadcrumb.support",
  counselor: "breadcrumb.counselor", more: "breadcrumb.more",
  // S-21 — the remaining sidebar-reachable segments (surfaced by the
  // nav-coherence vitest sweep; mostly second segments of /admin/* routes).
  "adaptive-path": "breadcrumb.adaptivePath", "ai-governance": "breadcrumb.aiGovernance",
  "ai-usage": "breadcrumb.aiUsage",
  "analytics-admin": "breadcrumb.analyticsAdmin", applications: "breadcrumb.applications",
  "auto-rules": "breadcrumb.autoRules", broadcast: "breadcrumb.broadcast",
  "bulk-enroll": "breadcrumb.bulkEnroll", claims: "breadcrumb.claims",
  clearance: "breadcrumb.clearance",
  "cognitive-profile": "breadcrumb.cognitiveProfile", cohorts: "breadcrumb.cohorts",
  "community-moderation": "breadcrumb.communityModeration", content: "breadcrumb.content",
  "content-analytics": "breadcrumb.contentAnalytics", "content-review": "breadcrumb.contentReview",
  copilot: "breadcrumb.copilot", "counseling-appointments": "breadcrumb.counselingAppointments",
  "counselor-caseload": "breadcrumb.counselorCaseload", "counselor-inbox": "breadcrumb.counselorInbox",
  "course-content": "breadcrumb.courseContent", courses: "breadcrumb.courses",
  "crisis-queue": "breadcrumb.crisisQueue",
  decrees: "breadcrumb.decrees", departments: "breadcrumb.departments",
  enrollments: "breadcrumb.enrollments", faculties: "breadcrumb.faculties",
  "faculty-reports": "breadcrumb.facultyReports", "flagged-reports": "breadcrumb.flaggedReports",
  gradebook: "breadcrumb.gradebook", grades: "breadcrumb.grades",
  "instructor-assignment": "breadcrumb.instructorAssignment",
  instructors: "breadcrumb.instructors", integrations: "breadcrumb.integrations",
  invoices: "breadcrumb.invoices",
  loans: "breadcrumb.loans", "ministry-library": "breadcrumb.ministryLibrary",
  "moderation-log": "breadcrumb.moderationLog",
  monitoring: "breadcrumb.monitoring", "capabilities-status": "breadcrumb.capabilitiesStatus",
  "my-chart": "breadcrumb.myChart",
  "my-payslips": "breadcrumb.myPayslips", network: "breadcrumb.network",
  "notif-templates": "breadcrumb.notifTemplates", offerings: "breadcrumb.offerings",
  "parent-links": "breadcrumb.parentLinks", payroll: "breadcrumb.payroll",
  "payroll-config": "breadcrumb.payrollConfig", plans: "breadcrumb.plans",
  portfolio: "breadcrumb.portfolio",
  "probation-review": "breadcrumb.probationReview", profiles: "breadcrumb.profiles",
  "reference-data": "breadcrumb.referenceData", refund: "breadcrumb.refund",
  reports: "breadcrumb.reports",
  review: "breadcrumb.review", roles: "breadcrumb.roles", "rubric-builder": "breadcrumb.rubricBuilder",
  schedule: "breadcrumb.schedule", seats: "breadcrumb.seats",
  specializations: "breadcrumb.specializations",
  "stage-progress": "breadcrumb.stageProgress", "student-overview": "breadcrumb.studentOverview",
  students: "breadcrumb.students", super: "breadcrumb.super", tenants: "breadcrumb.tenants",
  tickets: "breadcrumb.tickets", treasury: "breadcrumb.treasury",
  "user-lookup": "breadcrumb.userLookup",
  users: "breadcrumb.users",
};

/**
 * Label for one crumb. Pass the `shell` `t` from a component so the row
 * re-renders on a language switch; the argument-less form (non-React callers)
 * falls back to the imperative `translate` seam, which reads the same
 * catalogue but does not subscribe to changes.
 *
 * An unknown id returns the id itself — the Phase-A contract, unchanged.
 */
export const breadcrumbLabel = (id: string, t?: TFunction): string => {
  const key = BREADCRUMB_LABELS[id];
  if (!key) return id;
  const qualified = key.includes(":") ? key : "shell:" + key;
  return t ? String(t(qualified)) : translate(qualified, id);
};
